import type { FC, MutableRefObject } from 'react';
import React, { useCallback, useEffect, useState } from 'react';

import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import AnalyticsListener from '@atlaskit/analytics-next/AnalyticsListener';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';

import { getUniquePageLoadId } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId.ts';

/**
 * A mutable ref that `RendererTTIMarker` populates with a "the Atlaskit renderer has finished a
 * render pass" notifier. Consumers create the ref once (`useRef(null)`, or an instance field on a
 * class component), pass it in, and call `ref.current?.()` from the renderer's `onRendered`
 * callback.
 *
 * The indirection is deliberate: it keeps the post-paint readiness state *inside*
 * `RendererTTIMarker`. Holding that state in a consumer re-renders the entire renderer subtree
 * after paint, which lands inside the VC90 measurement window and measurably regresses it — see
 * COPPER-1053. Because `children` is passed in as an element, a re-render of `RendererTTIMarker`
 * alone lets React bail out on the whole document subtree.
 */
export type RendererRenderedSignalRef = MutableRefObject<(() => void) | null>;

/**
 * This component is wrapped around the TWP Atlaskit Fabric Renderer to detect when the renderer
 * has finished rendering the current revision. When it has, this component renders the results of
 * renderWhenReady() and releases its UFO load hold.
 *
 * Readiness has two possible sources, both recorded in this component's own state so that neither
 * costs more to flip than the other:
 *
 * - the `renderer` `rendered` analytics event — the original source. It is sampled, so it cannot be
 *   relied on alone, but it remains valid readiness when it does arrive.
 * - `renderedSignalRef` — driven by the renderer's `onRendered` lifecycle callback, which fires on
 *   every render pass regardless of analytics sampling. This is the reliable source.
 *
 * Readiness must stay in local state. Hoisting it into a consumer re-renders the whole renderer
 * subtree after paint, inside the VC90 measurement window, which regressed Edit Page VC90 by 182 ms
 * in COPPER-1053.
 */
export const RendererTTIMarker: FC<{
	revision?: string;
	renderWhenReady: () => React.ReactNode;
	children?: React.ReactNode;
	/**
	 * Populated by this component with a notifier to call from the renderer's `onRendered`
	 * callback. See {@link RendererRenderedSignalRef}.
	 */
	renderedSignalRef?: RendererRenderedSignalRef;
	/**
	 * Used only to reset recorded readiness when the rendered content changes. `revision` values
	 * are per-content version identifiers and can repeat across different content, so without this
	 * a revision recorded for previously-rendered content could satisfy `isReady` immediately for
	 * new content and release the hold before the renderer has run.
	 */
	contentId?: string;
}> = ({ renderWhenReady, revision = null, children, renderedSignalRef, contentId }) => {
	const [currentRenderedRevision, setCurrentRenderedRevision] = useState<string | null>(null);
	const [renderedRevision, setRenderedRevision] = useState<string | null>(null);

	// Hand the consumer a notifier that records the revision current at the moment the renderer
	// reports completion. Kept in this component so the resulting re-render does not reach children.
	useEffect(() => {
		if (!renderedSignalRef) {
			return;
		}
		renderedSignalRef.current = () => {
			setRenderedRevision(revision);
		};
		return () => {
			renderedSignalRef.current = null;
		};
	}, [renderedSignalRef, revision]);

	useEffect(() => {
		setRenderedRevision(null);
		setCurrentRenderedRevision(null);
	}, [contentId]);

	const handleEvent = useCallback(
		(event: UIAnalyticsEvent) => {
			if (event.payload.action === 'rendered' && event.payload.actionSubject === 'renderer') {
				// Store the current revision so we know what content just rendered
				setCurrentRenderedRevision(revision);

				// adding uniquePageLoad info to renderer rendered event
				event.update((payload) => {
					return {
						...payload,
						attributes: {
							...payload.attributes,
							pageLoadInfo: getUniquePageLoadId(),
						},
					};
				});
			}
		},
		[revision, setCurrentRenderedRevision],
	);

	// The content revision prop updates earlier than the renderer signals completion.
	// We aren't "ready" until the current revision has rendered.
	const isReady =
		revision !== null && (renderedRevision === revision || currentRenderedRevision === revision);
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const shouldInvokeRenderWhenReady = isReady || process.env.REACT_SSR;

	return (
		<UFOLoadHold name="RendererTTIMarker" hold={!shouldInvokeRenderWhenReady}>
			<AnalyticsListener channel="editor" onEvent={handleEvent}>
				{children}
				{shouldInvokeRenderWhenReady && renderWhenReady()}
			</AnalyticsListener>
		</UFOLoadHold>
	);
};
